import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
// One named icon per import so Vite tree-shakes the rest of the lucide set away.
import { CalendarPlus } from 'lucide-react'
import { Loader2 } from 'lucide-react'
import { MessageSquare } from 'lucide-react'
import { Search } from 'lucide-react'
import { TriangleAlert } from 'lucide-react'
import { UserPlus } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/Spinner'
import { ChoiceButton, SectionLabel } from '@/components/bookings/dialogKit'
import { NewClientDialog } from '@/components/bookings/NewClientDialog'
import { useCustomerSearch, MIN_QUERY_LENGTH, type CustomerMatch } from '@/hooks/useCustomerSearch'
import {
  createBooking,
  getOpenSlots,
  listServices,
  PAGE_MAX,
  type ServiceRecord,
} from '@/lib/api'
import { apiErrorReason, readApiError } from '@/lib/apiError'
import { fmtDayDate, fmtDuration, fmtMoney } from '@/lib/format'
import {
  findExactSlot,
  localDateTime,
  minutesFromLocalTime,
  minutesToLabel,
  nearestSlots,
  servicesForResource,
  sortServices,
  type OpenSlot,
  type SlotPick,
} from '@/lib/slotBooking'
import { cn } from '@/lib/utils'

// ─── "Book this slot" ──────────────────────────────────────────────────────────
//
// Opened by clicking empty space on the operator calendar. A fifth user-granted
// exception to the read-only data-screen rule (2026-08-10), and it is contained
// by the same line the payment dialog holds: EVERY input is a CHOICE from a
// server-supplied list, never a value the operator types and can get wrong.
//
//   which customer — picked from the customer search (the Customers screen's own
//                    search); an operator does not know phone numbers
//   which service  — picked from the machine's own services; price and duration
//                    come from the catalogue, never from this dialog
//   which time     — picked from GET /v1-availability; the clicked time is only a
//                    PROPOSAL and is confirmed against real slots before it is
//                    offered
//
// There is no amount, no duration box, no machine picker and no free text.
//
// THE CLICKED MACHINE IS NOT THE BOOKED MACHINE. Resources are auto-selected
// server-side (a Critical Rule — POST /v1-bookings accepts no resource_ids), so
// the column only narrows the service list. The copy here must never promise the
// machine that was clicked; the success toast names the machine the server chose.
//
// WHY AVAILABILITY IS RE-QUERIED RATHER THAN INFERRED: the calendar knows about
// bookings and nothing else. Business hours, per-resource schedules, time blocks
// and buffers all live in the availability computation, so an empty-looking gap
// is routinely unbookable. Offering it and letting the POST fail would teach the
// operator the calendar lies; asking the API and showing the real slots does not.

export interface NewBookingDialogProps {
  /** The clicked slot, or null when the dialog is closed. */
  pick: SlotPick | null
  onOpenChange: (open: boolean) => void
  /** Refetch the calendar once a booking lands. */
  onCreated?: () => void
  /**
   * Start with this customer already chosen, skipping the search. Set by "Paste
   * client" — the operator has already said who this is, on another booking.
   */
  presetCustomer?: CustomerMatch | null
}

export function NewBookingDialog({ pick, onOpenChange, onCreated, presetCustomer }: NewBookingDialogProps) {
  const navigate = useNavigate()
  const open = pick !== null

  const [services, setServices] = useState<ServiceRecord[] | null>(null)
  const [servicesError, setServicesError] = useState<string | null>(null)
  const [serviceId, setServiceId] = useState<string | null>(null)
  const [showAllServices, setShowAllServices] = useState(false)

  const [slots, setSlots] = useState<OpenSlot[] | null>(null)
  const [slotsLoading, setSlotsLoading] = useState(false)
  const [slotsError, setSlotsError] = useState<string | null>(null)
  const [chosenStart, setChosenStart] = useState<number | null>(null)

  const [customer, setCustomer] = useState<CustomerMatch | null>(null)
  const [query, setQuery] = useState('')
  const [newClientOpen, setNewClientOpen] = useState(false)
  const search = useCustomerSearch(query, { enabled: open && customer === null, limit: 5 })

  const [creating, setCreating] = useState(false)

  // Reset on every open. A dialog that remembers last time's service and
  // customer is a dialog that books the wrong appointment on a fast double-tap.
  // The pasted customer is the one exception: it was chosen deliberately for
  // THIS click, so it seeds the selection rather than being cleared.
  useEffect(() => {
    if (!open) return
    setServiceId(null)
    setShowAllServices(false)
    setSlots(null)
    setSlotsError(null)
    setChosenStart(null)
    setCustomer(presetCustomer ?? null)
    setQuery('')
    setNewClientOpen(false)
    setCreating(false)
  }, [open, pick?.date, pick?.startMinutes, pick?.resourceId, presetCustomer])

  // Active services only — 457 of this tenant's services are retired Momence
  // imports and `v1-bookings` refuses a retired one, so listing them would offer
  // a service that cannot be booked.
  useEffect(() => {
    if (!open || services !== null) return
    let live = true
    setServicesError(null)
    listServices(false, { limit: PAGE_MAX })
      .then((page) => { if (live) setServices(page.items) })
      .catch((e) => { if (live) setServicesError(readApiError(e)) })
    return () => { live = false }
  }, [open, services])

  const narrowed = useMemo(
    () => (services && pick ? servicesForResource(services, pick.resourceId) : []),
    [services, pick],
  )
  const allActive = useMemo(() => (services ? sortServices(services) : []), [services])
  // Nothing runs on this machine, so the narrowed list would be an empty panel
  // with no way forward. Fall back to the full catalogue rather than a dead end.
  const forceAll = services !== null && narrowed.length === 0
  const visibleServices = showAllServices || forceAll ? allActive : narrowed

  const service = useMemo(
    () => (serviceId ? allActive.find((s) => s.id === serviceId) ?? null : null),
    [serviceId, allActive],
  )

  // Exactly one service on this machine IS the answer — auto-select it so the
  // common case is two taps (customer, book). More than one is a real choice and
  // must never be guessed: the variants differ in price and duration.
  useEffect(() => {
    if (!open || serviceId !== null) return
    if (narrowed.length === 1) setServiceId(narrowed[0].id)
  }, [open, serviceId, narrowed])

  // Real open slots for the chosen service across the whole clicked day. The
  // window is the entire day, never a window around the click: a narrow window
  // returns a legitimate `no_availability` on a wide-open day (the widened-window
  // lesson from the widget), and the alternatives are the point of this call.
  useEffect(() => {
    if (!open || !pick || !serviceId) return
    let live = true
    setSlotsLoading(true)
    setSlotsError(null)
    setSlots(null)
    setChosenStart(null)
    getOpenSlots({ serviceId, from: `${pick.date}T00:00`, to: `${pick.date}T23:59` })
      .then((res) => {
        if (!live) return
        const found = res?.available_slots ?? []
        setSlots(found)
        // Pre-select the clicked time only when it is genuinely open. Otherwise
        // leave it unselected so the operator picks a real slot deliberately.
        setChosenStart(findExactSlot(found, pick.startMinutes) ? pick.startMinutes : null)
      })
      .catch((e) => { if (live) setSlotsError(readApiError(e)) })
      .finally(() => { if (live) setSlotsLoading(false) })
    return () => { live = false }
  }, [open, serviceId, pick?.date, pick?.startMinutes])

  const close = useCallback(() => {
    setCreating(false)
    onOpenChange(false)
  }, [onOpenChange])

  const clickedIsOpen = slots !== null && pick !== null && findExactSlot(slots, pick.startMinutes) !== null
  const alternatives = useMemo(
    () => (slots && pick ? nearestSlots(slots, pick.startMinutes, 6) : []),
    [slots, pick],
  )

  const canBook = pick !== null && service !== null && chosenStart !== null && customer !== null && !creating

  async function book() {
    if (!canBook || !pick || !service || chosenStart === null || !customer) return
    setCreating(true)
    try {
      const created = await createBooking({
        customer_id: customer.id,
        service_id: service.id,
        start: localDateTime(pick.date, chosenStart),
      })
      // Name the machine the SERVER chose, not the column that was clicked.
      const machines = created.resources?.map((r) => r.name).join(', ')
      toast.success(`${customer.name ?? 'Customer'} booked — ${minutesToLabel(chosenStart)}`, {
        description: [service.name, machines].filter(Boolean).join(' · '),
      })
      onCreated?.()
      close()
    } catch (e) {
      const reason = apiErrorReason(e)
      if (reason === 'slot_unavailable') {
        // Somebody took it between the availability read and the write. The
        // atomic RPC is what caught it; re-read so the operator sees the truth
        // rather than a stale list.
        toast.error('That slot has just been taken', { description: 'Pick another time.' })
        setChosenStart(null)
        if (pick) {
          getOpenSlots({ serviceId: service.id, from: `${pick.date}T00:00`, to: `${pick.date}T23:59` })
            .then((res) => setSlots(res?.available_slots ?? []))
            .catch(() => { /* the toast already said what happened */ })
        }
      } else if (reason === 'setup_required') {
        toast.error('Bookings are locked until setup is finished', { description: readApiError(e) })
      } else {
        toast.error("Couldn't create the booking", { description: readApiError(e) })
      }
      setCreating(false)
    }
  }

  function askInChat() {
    if (!pick) return
    const when = `${fmtDayDate(pick.date)} at ${minutesToLabel(pick.startMinutes)}`
    const parts = [`Book an appointment on ${when}.`]
    if (service) parts.push(`Service: ${service.name}.`)
    if (customer) parts.push(`Customer: ${customer.name ?? 'unnamed'}.`)
    if (pick.resourceName) parts.push(`I clicked the ${pick.resourceName} column.`)
    // Every id still goes in the message TEXT (chat history is text-only, so an
    // id that reaches the model only through a tool result is an id it invents
    // later) — but in the trailing [Screen context: …] line the panel strips
    // for display, so the operator reads names, never uuids.
    const ids: string[] = []
    if (service) ids.push(`service_id: ${service.id}`)
    if (customer) ids.push(`customer_id: ${customer.id}`)
    const text = ids.length
      ? `${parts.join(' ')}\n\n[Screen context: booking from the calendar (${ids.join(', ')}) — use these ids directly]`
      : parts.join(' ')
    close()
    navigate(`/chat?ask=${encodeURIComponent(text)}`)
  }

  return (
    <>
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New booking</DialogTitle>
          <DialogDescription>
            {pick
              ? `${fmtDayDate(pick.date)} · ${minutesToLabel(pick.startMinutes)}`
              : ''}
          </DialogDescription>
        </DialogHeader>

        {pick ? (
          <div className="flex flex-col gap-4">
            {/* ── Service ─────────────────────────────────────────────────── */}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <SectionLabel>Service</SectionLabel>
                {pick.resourceName && !showAllServices && !forceAll ? (
                  <span className="truncate text-[11px] text-muted-foreground">
                    on {pick.resourceName}
                  </span>
                ) : null}
              </div>

              {servicesError ? (
                <p className="text-[12.5px] text-destructive">{servicesError}</p>
              ) : services === null ? (
                <div className="flex items-center gap-2 py-2 text-[12.5px] text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  Loading the catalogue…
                </div>
              ) : (
                <>
                  {forceAll && pick.resourceName ? (
                    <p className="text-[11.5px] text-muted-foreground">
                      No service is set up to run on {pick.resourceName}, so this is the whole
                      catalogue.
                    </p>
                  ) : null}

                  {visibleServices.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-[12px] text-muted-foreground">
                      There are no active services to book. Add one in chat first.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-1.5">
                      {visibleServices.map((s) => (
                        <ChoiceButton
                          key={s.id}
                          title={s.name}
                          subtitle={`${fmtDuration(s.duration_minutes)} · ${fmtMoney(s.price, s.currency)}`}
                          selected={serviceId === s.id}
                          onClick={() => setServiceId(s.id)}
                        />
                      ))}
                    </div>
                  )}

                  {/* The machine narrows; it does not restrict. An operator
                      booking something unusual onto a free machine must not be
                      blocked by the filter. */}
                  {!forceAll && narrowed.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => setShowAllServices((v) => !v)}
                      className="self-start px-0.5 py-1 text-[12px] font-semibold text-blue-700"
                    >
                      {showAllServices
                        ? `Show only ${pick.resourceName ?? 'this machine'}'s services`
                        : `Show all services (${allActive.length})`}
                    </button>
                  ) : null}
                </>
              )}
            </div>

            {/* ── Time ────────────────────────────────────────────────────── */}
            {service ? (
              <div className="flex flex-col gap-1.5">
                <SectionLabel>Time</SectionLabel>

                {slotsLoading ? (
                  <div className="flex items-center gap-2 py-2 text-[12.5px] text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                    Checking what is actually free…
                  </div>
                ) : slotsError ? (
                  <p className="text-[12.5px] text-destructive">{slotsError}</p>
                ) : slots !== null && slots.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-[12px] text-muted-foreground">
                    {service.name} has nothing free on {fmtDayDate(pick.date)} — the day is fully
                    booked, blocked, or outside business hours.
                  </p>
                ) : slots !== null ? (
                  <>
                    {clickedIsOpen ? (
                      <ChoiceButton
                        title={minutesToLabel(pick.startMinutes)}
                        subtitle={`Free · ${fmtDuration(service.duration_minutes)}`}
                        selected={chosenStart === pick.startMinutes}
                        onClick={() => setChosenStart(pick.startMinutes)}
                      />
                    ) : (
                      <div className="flex items-start gap-2 rounded-lg bg-muted px-3 py-2">
                        <TriangleAlert className="mt-px size-3.5 shrink-0 text-amber-600" aria-hidden />
                        <p className="text-[11.5px] text-muted-foreground">
                          {minutesToLabel(pick.startMinutes)} is not bookable for {service.name} —
                          business hours, the machine's own schedule, a time block or a buffer rules
                          it out. The nearest free times are below.
                        </p>
                      </div>
                    )}

                    {(clickedIsOpen ? alternatives.filter(s => minutesFromLocalTime(s.start) !== pick.startMinutes) : alternatives).length > 0 ? (
                      <>
                        {clickedIsOpen ? <SectionLabel>Or nearby</SectionLabel> : null}
                        <div className="flex flex-wrap gap-1.5">
                          {alternatives
                            .filter((s) => !clickedIsOpen || minutesFromLocalTime(s.start) !== pick.startMinutes)
                            .map((s) => {
                              const mins = minutesFromLocalTime(s.start)
                              return (
                                <button
                                  key={s.start}
                                  type="button"
                                  aria-pressed={chosenStart === mins}
                                  onClick={() => setChosenStart(mins)}
                                  className={cn(
                                    'rounded-lg border px-2.5 py-1.5 text-[12.5px] font-semibold transition-colors',
                                    chosenStart === mins
                                      ? 'border-primary bg-primary/10 text-foreground'
                                      : 'border-border bg-card text-muted-foreground hover:bg-muted',
                                  )}
                                >
                                  {minutesToLabel(mins)}
                                </button>
                              )
                            })}
                        </div>
                      </>
                    ) : null}
                  </>
                ) : null}
              </div>
            ) : null}

            {/* ── Customer ────────────────────────────────────────────────── */}
            <div className="flex flex-col gap-1.5">
              <SectionLabel>Customer</SectionLabel>
              {customer ? (
                <div className="flex items-center justify-between gap-2 rounded-lg border border-primary bg-primary/5 px-3 py-2">
                  <span className="min-w-0">
                    <span className="block truncate text-[13.5px] font-semibold text-foreground">
                      {customer.name ?? 'Unnamed customer'}
                    </span>
                    {customer.phone ? (
                      <span className="block truncate text-[11.5px] text-muted-foreground">
                        {customer.phone}
                      </span>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setCustomer(null); setQuery('') }}
                    className="shrink-0 px-1 py-0.5 text-[12px] font-semibold text-blue-700"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <div className="flex flex-1 items-center gap-2 rounded-lg border border-border bg-background px-2.5 py-1.5">
                      <Search className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by name, email or phone…"
                        aria-label="Search customers"
                        className="w-full bg-transparent text-[14px] text-foreground outline-none placeholder:text-muted-foreground"
                      />
                    </div>
                    {/* The walk-in case. Creating a client is not new — chat and
                        MCP both have create_customer — this is just the route
                        that does not make the operator leave the booking. */}
                    <button
                      type="button"
                      onClick={() => setNewClientOpen(true)}
                      className="flex shrink-0 items-center gap-1 rounded-lg border border-border bg-card px-2.5 py-1.5 text-[12.5px] font-semibold text-foreground hover:bg-muted"
                    >
                      <UserPlus className="size-3.5" aria-hidden />
                      New
                    </button>
                  </div>
                  {search.searching ? (
                    <div className="flex items-center gap-2 px-1 py-1 text-[12.5px] text-muted-foreground">
                      <Spinner size={14} className="text-current" />
                      Searching…
                    </div>
                  ) : !search.active ? (
                    <p className="px-1 text-[11.5px] text-muted-foreground">
                      Type at least {MIN_QUERY_LENGTH} characters, or tap New for someone who has
                      not been in before.
                    </p>
                  ) : search.matches.length === 0 ? (
                    <p className="px-1 text-[12px] text-muted-foreground">
                      No customers match — tap New to add them.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-1.5">
                      {search.matches.map((m) => (
                        <ChoiceButton
                          key={m.id}
                          title={m.name ?? 'Unnamed customer'}
                          subtitle={m.phone ?? undefined}
                          onClick={() => { setCustomer(m); setQuery('') }}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── Confirm ─────────────────────────────────────────────────── */}
            <div className="flex flex-col gap-2 border-t border-border pt-3">
              <button
                type="button"
                disabled={!canBook}
                onClick={() => void book()}
                className={cn(
                  'flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2.5',
                  'text-[14px] font-semibold text-primary-foreground',
                  'bg-primary hover:bg-primary/90',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
              >
                {creating ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : (
                  <CalendarPlus className="size-4" aria-hidden />
                )}
                {chosenStart !== null && service && customer
                  ? `Book ${minutesToLabel(chosenStart)}`
                  : 'Book this slot'}
              </button>

              {/* The machine is chosen by the server, so saying which column was
                  clicked would be a promise this dialog cannot keep. */}
              {service && chosenStart !== null ? (
                <p className="text-center text-[11px] text-muted-foreground">
                  The machine is assigned automatically from what is free.
                </p>
              ) : null}

              {/* The escape hatch that keeps this dialog small. A price override,
                  a package to attach, a note to file — all of it is chat's job. */}
              <button
                type="button"
                onClick={askInChat}
                className="flex items-center gap-1.5 self-center px-0.5 py-1 text-[12.5px] font-semibold text-blue-700"
              >
                <MessageSquare className="size-3.5" aria-hidden />
                Something else — ask in chat
              </button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>

    {/*
      Mounted OUTSIDE the booking dialog's content. Radix unmounts a dialog's
      children when it closes, and a nested dialog inside them goes with it —
      the same reason PaymentDialog sits outside the quick-action menu.
    */}
    <NewClientDialog
      open={newClientOpen}
      onOpenChange={setNewClientOpen}
      onCreated={(created) => { setCustomer(created); setQuery('') }}
    />
    </>
  )
}

/** Re-exported so the calendar views can type their callback without a deep import. */
export type { SlotPick }
